/* global window */
/// <reference types="webpack-env" />
import { compose, createStore, applyMiddleware, Reducer, AppDispatch, AnyAction } from 'redux';
import { Map } from 'immutable';
import rootReducer from 'app/reducers';
import errorMiddleware from 'app/middleware/errors';
import ReduxThunk, { ThunkAction } from 'redux-thunk';
import appConfig from 'app/config/client';
import { RunningIn } from 'app/config/types/DeployInfo';

// Extend Window and NodeModule here because it doesn't work in global.d.ts
declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    // For some reason compose in undefined in ESLint...
    // eslint-disable-next-line no-undef
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

export type AppState = Map<any, any>;

/**
 * A reusable type than can be used when writing thunk actions
 */
export type AppThunk<R> = ThunkAction<R, AppState, unknown, AnyAction>;

const createAppStore = (initialState: AppState = Map(), reducer: Reducer<AppState> = rootReducer) => {
  const middleware = [errorMiddleware, ReduxThunk];
  let composeEnhancers = compose;

  if (typeof window !== 'undefined' && appConfig.deployInfo.runningIn !== RunningIn.Production) {
    // eslint-disable-next-line no-underscore-dangle
    composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || composeEnhancers;
  }

  const store = createStore(
    reducer,
    initialState,
    composeEnhancers(
      applyMiddleware<AppDispatch>(...middleware),
    ),
  );

  // Manually setup hot module replacement for reducers
  if (typeof module !== 'undefined' && module.hot && reducer === rootReducer) {
    module.hot.accept('./reducers/index', () => {
      const nextRootReducer =
              require('./reducers/index').rootReducer; // eslint-disable-line global-require
      store.replaceReducer(nextRootReducer);
    });
  }

  return store;
};

export type AppStore = ReturnType<typeof createAppStore>;

export default createAppStore;
